// src/services/notificationService.js
import axios from 'axios';
import { generateDeviceFingerprint, getDeviceName, isPWAInstalled } from '../utils/pwa-utils';
import { notificationsApi } from './apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.caro.care';

class NotificationService {
  constructor() {
    this.subscription = null;
    this.vapidPublicKey = null;
    this.serviceWorkerRegistration = null;
  }

  /**
   * Initialize notification service
   */
  async initialize() {
    try {
      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers not supported');
        return false;
      }

      // Check if push notifications are supported
      if (!('PushManager' in window)) {
        console.warn('Push notifications not supported');
        return false;
      }

      // Get service worker registration
      this.serviceWorkerRegistration = await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      await this.fetchVapidPublicKey();

      // Check if already subscribed
      const existingSubscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        this.subscription = existingSubscription;
        //console.log('Already subscribed to push notifications');
      }

      // Listen for messages from service worker
      this.setupServiceWorkerMessageListener();

      return true;
    } catch (error) {
      //console.error('Failed to initialize notification service:', error);
      return false;
    }
  }

  /**
   * Fetch VAPID public key from server
   */
  async fetchVapidPublicKey() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/notifications/vapid-public-key`);
      this.vapidPublicKey = response.data.publicKey;
      //('VAPID public key fetched');
    } catch (error) {
      console.error('Failed to fetch VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    try {
      if (!('Notification' in window)) {
        throw new Error('Notifications not supported');
      }

      const permission = await Notification.requestPermission();
      //console.log('Notification permission:', permission);

      return permission === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }

  /**
   * Subscribe to push notifications
   */
  async subscribe() {
    try {
      // Request permission first
      const permissionGranted = await this.requestPermission();
      if (!permissionGranted) {
        throw new Error('Notification permission denied');
      }

      // Initialize if not already done
      if (!this.vapidPublicKey) {
        await this.initialize();
      }

      // Convert VAPID key to Uint8Array
      const convertedVapidKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

      // Subscribe to push notifications
      this.subscription = await this.serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      //console.log('Push subscription created:', this.subscription);

      // Get device info
      const deviceInfo = {
        deviceFingerprint: await generateDeviceFingerprint(),
        deviceName: getDeviceName(),
        isPWA: isPWAInstalled(),
        userAgent: navigator.userAgent
      };

      // Send subscription to server using apiClient
      const response = await notificationsApi.subscribe({
        subscription: this.subscription.toJSON(),
        deviceInfo
      });

      //console.log('Subscription registered with server:', response.data);
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return false;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      if (!this.subscription) {
        console.warn('No active subscription to unsubscribe from');
        return true;
      }

      // Unsubscribe from push manager
      await this.subscription.unsubscribe();

      // Notify server using apiClient
      const endpoint = this.subscription.endpoint;
      await notificationsApi.unsubscribe(endpoint);

      this.subscription = null;
      //console.log('Unsubscribed from push notifications');
      return true;
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      return false;
    }
  }

  /**
   * Check if subscribed to push notifications
   */
  async isSubscribed() {
    try {
      if (!this.serviceWorkerRegistration) {
        await this.initialize();
      }

      const subscription = await this.serviceWorkerRegistration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription !== null;
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      return false;
    }
  }

  /**
   * Get notification history
   */
  async getNotifications(options = {}) {
    try {
      const { limit = 50, offset = 0, includeRead = true, type, priority } = options;

      // Use apiClient which handles authentication automatically
      const response = await notificationsApi.getAll({ limit, offset, includeRead, type, priority });

      return response.data;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      // Don't throw error on initial load, just return empty
      return { notifications: [], unreadCount: 0 };
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount() {
    try {
      // Use apiClient which handles authentication automatically
      const response = await notificationsApi.getUnreadCount();

      return response.data.count;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      return 0;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId) {
    try {
      // Use apiClient which handles authentication automatically
      await notificationsApi.markAsRead(notificationId);

      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      // Use apiClient which handles authentication automatically
      const response = await notificationsApi.markAllAsRead();

      return response.data;
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      return false;
    }
  }

  /**
   * Send test notification
   */
  async sendTestNotification(options = {}) {
    try {
      // This endpoint doesn't exist in apiClient yet, keep axios for now
      // but we could add it to apiClient later
      const response = await axios.post(
        `${API_BASE_URL}/api/notifications/test`,
        options
      );

      return response.data;
    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw error;
    }
  }

  /**
   * Get user's push subscriptions
   */
  async getSubscriptions() {
    try {
      // This endpoint doesn't exist in apiClient yet, keep axios for now
      const response = await axios.get(`${API_BASE_URL}/api/notifications/subscriptions`);

      return response.data.subscriptions;
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      return [];
    }
  }

  /**
   * Setup service worker message listener
   */
  setupServiceWorkerMessageListener() {
    if (!navigator.serviceWorker) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
      //console.log('Message from service worker:', event.data);

      switch (event.data.type) {
        case 'NOTIFICATION_CLICKED':
          this.handleNotificationClick(event.data);
          break;

        case 'PLAY_CRITICAL_SOUND':
          this.playCriticalSound();
          break;

        case 'MARK_NOTIFICATION_READ':
          this.markAsRead(event.data.notificationId);
          break;

        default:
          //console.log('Unknown message type:', event.data.type);
      }
    });
  }

  /**
   * Handle notification click
   */
  handleNotificationClick(data) {
    //console.log('Notification clicked:', data);

    // Navigate to the action URL if provided
    if (data.actionUrl) {
      window.location.href = data.actionUrl;
    }

    // Emit custom event for app to handle
    window.dispatchEvent(new CustomEvent('notificationClicked', { detail: data }));
  }

  /**
   * Play critical alert sound (overrides device mute)
   */
  playCriticalSound() {
    try {
      // Create an audio context with maxed settings
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create oscillator for beep sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure oscillator
      oscillator.frequency.value = 880; // A5 note (high pitch)
      oscillator.type = 'sine';

      // Configure gain (volume)
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      // Play the sound
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);

      // Play 3 beeps
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 880;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.5);
      }, 600);

      setTimeout(() => {
        const osc3 = audioContext.createOscillator();
        const gain3 = audioContext.createGain();
        osc3.connect(gain3);
        gain3.connect(audioContext.destination);
        osc3.frequency.value = 880;
        osc3.type = 'sine';
        gain3.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        osc3.start(audioContext.currentTime);
        osc3.stop(audioContext.currentTime + 0.5);
      }, 1200);

      //console.log('Critical alert sound played');
    } catch (error) {
      console.error('Failed to play critical sound:', error);
    }
  }

  /**
   * Convert URL-safe base64 to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }
}

export default new NotificationService();
